import React, { useState } from "react";
import {
  IconButton,
  Popover,
  Checkbox,
  Tooltip,
  Grid,
  Stack,
  Button,
  Box,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeMosaicSharpIcon from "@mui/icons-material/AutoAwesomeMosaicSharp";
import { FilterProps } from "src/shared_components/interfaces";
import { useTranslation } from "react-i18next";

interface FilterCustomizationProps {
  title?: string;
  columns?: FilterProps[];
  setColumns: React.Dispatch<React.SetStateAction<FilterProps[]>>;
  defaultColumns?: FilterProps[];
  icon?: React.ReactNode;
  ignoreFirst?: boolean;
  onApplyColumns?: (columns: FilterProps[]) => Promise<void> | void;
  onResetColumns?: (columns: FilterProps[]) => Promise<void> | void;
}

const FilterCustomization: React.FC<FilterCustomizationProps> = ({
  title,
  columns,
  setColumns,
  defaultColumns,
  icon,
  ignoreFirst,
  onApplyColumns,
  onResetColumns,
}) => {
  const isGreaterThanSm = useMediaQuery((theme: any) =>
    theme.breakpoints.up("sm")
  );
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedColumns, setSelectedColumns] = useState<
    FilterProps[] | undefined
  >(columns);
  const [isApplying, setIsApplying] = useState(false);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSelectedColumns(columns);
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setSelectedColumns(columns);
    setAnchorEl(null);
  };

  const onApply = async () => {
    const columnsToApply = selectedColumns || [];
    setIsApplying(true);
    try {
      setColumns(columnsToApply);
      await onApplyColumns?.(columnsToApply);
      setAnchorEl(null);
    } finally {
      setIsApplying(false);
    }
  };

  const handleColumnToggle = (column: any) => {
    setSelectedColumns((prevColumns) =>
      prevColumns?.map((c) =>
        c.id === column.id ? { ...c, visible: !column.visible } : c
      )
    );
  };

  const open = Boolean(anchorEl);

  const handleReset = async () => {
    const columnsToReset = defaultColumns || [];
    setIsApplying(true);
    try {
      setSelectedColumns(columnsToReset);
      setColumns(columnsToReset);
      await onResetColumns?.(columnsToReset);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <Tooltip title={title}>
        <IconButton onClick={handlePopoverOpen} color="primary" size="medium">
          {icon || <AutoAwesomeMosaicSharpIcon />}
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            style: {
              maxWidth: isGreaterThanSm ? "50vw" : "90vw",
            },
          },
        }}
      >
        <Box p={2}>
          <Grid container spacing={2}>
            {selectedColumns?.map(
              (column, i) =>
                column.id !== "actionButton" &&
                !(ignoreFirst && i == 0) && (
                  <Grid item xl={4} md={4} xs={6} key={i}>
                    <Box
                      component="div"
                      onClick={() => handleColumnToggle(column)}
                      style={{ cursor: "pointer" }}
                    >
                      <Checkbox checked={!!column.visible} />
                      {column.label}
                    </Box>
                  </Grid>
                )
            )}
          </Grid>
          <Stack direction="row" spacing={2} mt={2}>
            <Button
              variant="outlined"
              sx={{ width: "50%" }}
              color="error"
              onClick={handleReset}
              disabled={isApplying}
            >
              {t("common.buttons.reset")}
            </Button>
            <Button
              variant="outlined"
              sx={{ width: "50%" }}
              onClick={onApply}
              disabled={isApplying}
            >
              {t("common.buttons.apply")}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default FilterCustomization;
